import {
  Component,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ViewChild,
  AfterViewInit,
  ViewEncapsulation,
} from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import Quill from 'quill';
import TableUp, { defaultCustomSelect, TableAlign, TableMenuContextmenu, TableMenuSelect, TableResizeBox, TableResizeScale, TableSelection, TableVirtualScrollbar } from 'quill-table-up';
import { LanguageService } from '../services/language.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MathFieldBlot } from './math-field.blot';
import { QuestionserviceService } from '../services/questionservice.service';
import { attachMathfieldKeyboardListeners, closeVirtualKeyboard } from './math-field-keyboard';

Quill.register({ [`modules/${TableUp.moduleName}`]: TableUp }, true);
Quill.register(MathFieldBlot);

@Component({
  selector: 'app-quill-editor',
  templateUrl: './quill-editor.component.html',
  styleUrls: ['./quill-editor.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class QuillEditorComponent implements OnDestroy, AfterViewInit {

  @ViewChild('quillContainer', { static: true }) quillContainer!: ElementRef;
  @ViewChild('clearTextModal') clearTextModal: any;

  @Output() valueChange = new EventEmitter<[string, string]>();
  @Output() editorCreated = new EventEmitter();

  @Input() placeholder: string = '';
  @Input() readOnly: boolean = false;
  @Input() maxWordCountHighLight: boolean = true;
  @Input() maxWordCount: number = 0;
  @Input() readOnlyMessage: string = this.language.quillEditorReadOnlyDefaultMessage;

  private quill!: Quill;
  _mathVirtualKeyboard: any;

  private textChangeHandler: () => void;
  private keyboardOpen: boolean = false;
  private pageContainerDefaultHeight: string = '';

  constructor(private toastr: ToastrService, public languageService: LanguageService, private modalService: NgbModal,
    private elementRef: ElementRef) { }

  ngAfterViewInit(): void {
    this.initQuill();
    this._mathVirtualKeyboard = (window as any).mathVirtualKeyboard;

    this.pageContainerDefaultHeight = (document.querySelector('.page-height-control') as HTMLElement).style.height;
    this.editorCreated.emit();
    this.patchTooltipPositioning();
    this.observeTableMenu();
  }

  ngOnDestroy() {
    if (this.quill) {
      this.quill.off('text-change');
    }
  }

  get language() {
    return this.languageService.language;
  }
  private initQuill(): void {

    const modules = {
      toolbar: {
        container: [
          ['undo', 'redo'],
          ['bold', 'italic', 'underline', 'strike'],
          ['blockquote', 'code'],
          ['link', 'image'],
          [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
          [{ script: 'sub' }, { script: 'super' }],
          [{ indent: '-1' }, { indent: '+1' }],
          [{ direction: 'rtl' }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          [{ [TableUp.toolName]: [] }],
          ['mathField'],
          ['clean'],
          ['clear-text']
        ],
        handlers: {
          'undo': () => this.quill.history.undo(),
          'redo': () => this.quill.history.redo(),
          'clear-text': () => this.handleClearButtonClick(),
          'mathField': () => this.insertMathField(),
          history: {
            userOnly: true // leaves programmatical changes intact, including init'ing the content
          }
        }
      },
      [TableUp.moduleName]: {
        full: true,
        scrollbar: TableVirtualScrollbar,
        align: TableAlign,
        resize: TableResizeBox,
        resizeScale: TableResizeScale,
        customSelect: defaultCustomSelect,
        selection: TableSelection,
        selectionOptions: {
          tableMenu: TableMenuSelect,
        },
      },
      keyboard: {
        bindings: {
          tab: null
        }
      }
    };

    var icons = Quill.import('ui/icons');
    icons['undo'] = `<svg viewBox="0 0 80 80" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
                          <g transform="scale(-1,1) translate(-80,0)">
                            <path style="fill: #444;" class="ql-stroke" fill="#000000" d="M26,70.5C13.042,70.5,2.5,59.958,2.5,47S13.042,23.5,26,23.5h32.5V10.165L77.274,28L58.5,45.835 V32.5H26c-7.995,0-14.5,6.505-14.5,14.5S18.005,61.5,26,61.5h13.5v9H26z"></path>
                          </g>
                       </svg>`;
    icons['redo'] = `<svg viewBox="0 0 80 80" width="18" height="18">
                        <path style="fill: #444;"  class="ql-stroke" fill="#000000" d="M26,70.5C13.042,70.5,2.5,59.958,2.5,47S13.042,23.5,26,23.5h32.5V10.165L77.274,28L58.5,45.835 V32.5H26c-7.995,0-14.5,6.505-14.5,14.5S18.005,61.5,26,61.5h13.5v9H26z"></path>
                      </svg>`;

    this.quill = new Quill(this.quillContainer.nativeElement, {
      theme: 'snow',
      placeholder: this.placeholder,
      readOnly: this.readOnly,
      modules: modules
    });

    this.textChangeHandler = () => {

      const html = this.quill.root.innerHTML;
      const plainText = this.quill.getText().trim();

      this.valueChange.emit([html, plainText]);
      if (this.maxWordCountHighLight) {
        const length = this.getWordCount(plainText);
        const highLight = length[0] > this.maxWordCount && this.maxWordCount > 0;

        // // Reset text color
        // this.quill.formatText(0, length[1], {
        //   'color': 'rgb(0,0,0)'
        // });

        // Highlight excess text
        if (highLight) {
          this.quill.formatText(length[1], length[2], {
            'color': 'rgb(220, 20, 60)'
          });
        }
      }
    };

    this.quill.on('text-change', this.textChangeHandler);
    this.setupAdditionalToolbarOptions();
  }

  setValue(value: string) {

    if (this.quill) {
      if (this.quill.root.innerHTML !== value) {
        this.quill.setText('');
        this.quill.history.clear();
        this.quill.root.innerHTML = this.sanitizeHTML(value);
        this.initializeMathFields();
      }
    }
  }


  private initializeMathFields(): void {
    // Find all math-field elements in the editor
    const mathFields: any = this.quill.root.querySelectorAll('math-field');
    const isInIframe = window !== window.top;

    mathFields.forEach(mathField => {
       // CRITICAL: Use 'sandboxed' policy in iframe to create local keyboard
    if (isInIframe) {
        mathField.mathVirtualKeyboardPolicy = 'sandboxed';
      } else {
        mathField.mathVirtualKeyboardPolicy = 'manual';
      }
      // console.log('MathField policy set to:', mathField.mathVirtualKeyboardPolicy);

      // Add input event listener to ensure content changes trigger Quill updates
      mathField.addEventListener('input', () => {

        // we are doing this instead of just setting mathField.innerHTML because the version of this quill 
        // takes the focus off math-field once anythhing in it changes including element properties.
        // Hence the need for managing the input event for mathfield outside quill
        QuestionserviceService.updateMathFieldValues(mathField.id);
      });

      attachMathfieldKeyboardListeners({
        mathField: mathField,
        keyboard: this._mathVirtualKeyboard,
        isOpen: () => this.keyboardOpen,
        setOpen: v => this.keyboardOpen = v,
        //updateLayout: () => this.updateMathFieldKeyboard(),
        pageContainerSelector: '.page-height-control',
        pageOriginalHeight: this.pageContainerDefaultHeight
      });
    });
  }

  private setupAdditionalToolbarOptions() {
    setTimeout(() => {
      if (!this.quill) return;

      const quillElement = this.elementRef.nativeElement;

      const undoButtonEl = quillElement.querySelector('.ql-undo') as HTMLElement;
      if (undoButtonEl) {
        undoButtonEl.title = this.language.undo;
      };

      const redoButtonEl = quillElement.querySelector('.ql-redo') as HTMLElement;
      if (redoButtonEl) {
        redoButtonEl.title = this.language.redo;
      };

      const insertTableSpanEl = quillElement.querySelector('.ql-table-up') as HTMLElement;
      if (insertTableSpanEl) {
        insertTableSpanEl.title = this.language.insertTable;
      };

      const clearAnswerButtonEl = quillElement.querySelector('.ql-clear-text') as HTMLElement;
      if (clearAnswerButtonEl) {
        clearAnswerButtonEl.classList.add(
          'btn',
          'btn-sm',
          'text-danger',
          'text-nowrap',
          'fw-bold',
          'w-100',
          'border',
          'border-danger',
        );
        clearAnswerButtonEl.title = this.language.clearAnswer;
        clearAnswerButtonEl.innerText = this.language.clearAnswer;
      }

      const mathfieldToolbarEl = quillElement.querySelector('.ql-mathField');
      if (mathfieldToolbarEl) {
        mathfieldToolbarEl.innerHTML = `
          <svg viewBox="0 0 18 18"> 
            <path class="ql-fill" d="M11.759,2.482a2.561,2.561,0,0,0-3.53.607A7.656,7.656,0,0,0,6.8,6.2C6.109,9.188,5.275,14.677,4.15,14.927a1.545,1.545,0,0,0-1.3-.933A0.922,0.922,0,0,0,2,15.036S1.954,16,4.119,16s3.091-2.691,3.7-5.553c0.177-.826.36-1.726,0.554-2.6L8.775,6.2c0.381-1.421.807-2.521,1.306-2.676a1.014,1.014,0,0,0,1.02.56A0.966,0.966,0,0,0,11.759,2.482Z"></path> 
            <rect class="ql-fill" height="1.6" rx="0.8" ry="0.8" width="5" x="5.15" y="6.2"></rect> 
            <path class="ql-fill" d="M13.663,12.027a1.662,1.662,0,0,1,.266-0.276q0.193,0.069.456,0.138a2.1,2.1,0,0,0,.535.069,1.075,1.075,0,0,0,.767-0.3,1.044,1.044,0,0,0,.314-0.8,0.84,0.84,0,0,0-.238-0.619,0.8,0.8,0,0,0-.594-0.239,1.154,1.154,0,0,0-.781.3,4.607,4.607,0,0,0-.781,1q-0.091.15-.218,0.346l-0.246.38c-0.068-.288-0.137-0.582-0.212-0.885-0.459-1.847-2.494-.984-2.941-0.8-0.482.2-.353,0.647-0.094,0.529a0.869,0.869,0,0,1,1.281.585c0.217,0.751.377,1.436,0.527,2.038a5.688,5.688,0,0,1-.362.467,2.69,2.69,0,0,1-.264.271q-0.221-.08-0.471-0.147a2.029,2.029,0,0,0-.522-0.066,1.079,1.079,0,0,0-.768.3A1.058,1.058,0,0,0,9,15.131a0.82,0.82,0,0,0,.832.852,1.134,1.134,0,0,0,.787-0.3,5.11,5.11,0,0,0,.776-0.993q0.141-.219.215-0.34c0.046-.076.122-0.194,0.223-0.346a2.786,2.786,0,0,0,.918,1.726,2.582,2.582,0,0,0,2.376-.185c0.317-.181.212-0.565,0-0.494A0.807,0.807,0,0,1,14.176,15a5.159,5.159,0,0,1-.913-2.446l0,0Q13.487,12.24,13.663,12.027Z"></path> 
          </svg>
        `;
        mathfieldToolbarEl.setAttribute('title', 'Insert Equation');
      }
    }, 100); // delay slightly longer to ensure DOM is ready
  }

  handleClearButtonClick() {
    if (this.quill && !this.readOnly) {
      // Only show the confirmation modal if there's content to clear
      const editorHasContent = this.quill.getText().trim().length > 0;

      if (editorHasContent) {
        this.openClearTextModal();
      } else {
        // If editor is already empty, no need for confirmation
        this.clearText();
      }
    } else if (this.quill && this.readOnly) {
      this.toastr.error(this.readOnlyMessage, '', {
        timeOut: 3000,
        positionClass: 'toast-top-right',
        closeButton: true,
      });
    }
  }

  openClearTextModal() {
    this.modalService.open(this.clearTextModal, {
      centered: true,
      size: 'xs',
      keyboard: true,
      ariaLabelledBy: 'modal-clear-text'
    }).result.then(
      (result) => {
        if (result === 'yes') {
          this.clearText();
        }
      },
      (reason) => {
        // Modal dismissed
      }
    );
  }

  clearText() {
    if (this.quill && !this.readOnly) {
      this.quill.setText('');
      this.valueChange.emit(['', '']);
    } else if (this.quill && this.readOnly) {
      this.toastr.error(this.readOnlyMessage, '', {
        timeOut: 3000,
        positionClass: 'toast-top-right',
        closeButton: true,
      });
    }
  }

  getWordCount(text: string): [number, number, number] {
    let words = text.split(/\s+/);
    let wordlength = words.length;
    let allowedwords = this.maxWordCount > 0 ? text.split(/\s+/, this.maxWordCount) : words;
    let allowedxters = this.maxWordCount > 0 ? allowedwords.join().length : text.length;
    return !text ? [0, 0, 0] : [wordlength, allowedxters, text.length];
  }

  setReadOnly(isReadOnly: boolean): void {
    if (!this.quill) return;
    this.readOnly = isReadOnly;
    this.quill.enable(!isReadOnly);

    const qlEditorEl = this.quillContainer.nativeElement.querySelector('.ql-editor') as HTMLElement;
    if (qlEditorEl) {
      qlEditorEl.setAttribute('role', 'textbox');
      qlEditorEl.setAttribute('aria-label', this.language.answerArea);
      qlEditorEl.setAttribute('aria-readonly', isReadOnly ? 'true' : 'false');
      qlEditorEl.setAttribute('aria-multiline', 'true');
      if (isReadOnly) {
        qlEditorEl.setAttribute('aria-live', 'polite');
        qlEditorEl.removeAttribute('tabindex');
      } else {
        qlEditorEl.removeAttribute('aria-live');
        qlEditorEl.setAttribute('tabindex', '0');
      }
    }

    if (isReadOnly) {
      // Add event listeners to detect interaction attempts
      this.quillContainer.nativeElement.addEventListener(
        'click',
        this.handleReadOnlyInteraction
      );
      this.quillContainer.nativeElement.addEventListener(
        'focus',
        this.handleReadOnlyInteraction,
        true
      );
    } else {
      // Remove event listeners when not in read-only mode
      this.quillContainer.nativeElement.removeEventListener(
        'click',
        this.handleReadOnlyInteraction
      );
      this.quillContainer.nativeElement.removeEventListener(
        'focus',
        this.handleReadOnlyInteraction,
        true
      );
    }
  }

  private handleReadOnlyInteraction = (event: Event): void => {
    if (this.readOnly) {
      this.toastr.error(this.readOnlyMessage, '', {
        timeOut: 3000,
        positionClass: 'toast-top-right',
        closeButton: true,
      });

      // Prevent default behavior and stop event propagation
      event.preventDefault();
      event.stopPropagation();

      // Ensure editor stays in read-only mode
      this.quill?.enable(false);
    }
  };

  private patchTooltipPositioning() {
    const Tooltip: any = Quill.import('ui/tooltip');
    const originalPosition = Tooltip.prototype.position;
    Tooltip.prototype.position = function (reference: { left: number; width: number; bottom: number }) {
      originalPosition.call(this, reference);

      // Then check and correct ONLY if left position is negative
      const currentLeft = parseInt(this.root.style.left);
      if (currentLeft < 0) {
        this.root.style.left = '0px';
      } else {
        this.root.style.left = `${currentLeft - 12}px`; // take care of margin
      }
    };
  }

  private observeTableMenu(): void {
    const menu = document.querySelector('.table-up-menu');
    const toolbox = document.querySelector('.table-up-toolbox');
    const editor = document.querySelector('.ql-editor');

    if (!menu || !toolbox || !editor) return;

    const observer = new MutationObserver(() => {
      let topStr = (menu as HTMLElement).style.top;

      // Fallback to computed style
      if (!topStr) {
        topStr = window.getComputedStyle(menu as HTMLElement).top;
      }

      const top = parseFloat(topStr);
      const editorHeight = (editor as HTMLElement).offsetHeight;

      if (!isNaN(top)) {
        if (top < 0 || editorHeight < 180) {
          (toolbox as HTMLElement).style.overflow = 'visible';
        } else {
          (toolbox as HTMLElement).style.overflow = 'hidden'; // optional
        }
      }
    });

    observer.observe(menu, {
      attributes: true,
      attributeFilter: ['style'],
    });
  }

  private insertMathField() {
    const range = this.quill.getSelection(true);
    if(this.keyboardOpen)
    {
      closeVirtualKeyboard({
        mathField: null,
        keyboard: this._mathVirtualKeyboard,
        isOpen: () => this.keyboardOpen,
        setOpen: v => this.keyboardOpen = v,
        //updateLayout: () => this.updateMathFieldKeyboard(),
        pageContainerSelector: '.page-height-control',
        pageOriginalHeight: this.pageContainerDefaultHeight
      })
    }
    // Insert the input blot at current position
    this.quill.insertEmbed(range.index, 'input', {
      content: '',  // Initialize with empty content
      width: '150px',
      pageHeight: this.pageContainerDefaultHeight
    });
    // Insert a space after the math field
    this.quill.insertText(range.index + 1, ' ');

    // Move cursor after the inserted blot
    this.quill.setSelection(range.index, 1);

    //this.updateMathFieldKeyboard();
  }
  private sanitizeHTML(html) {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;

    // Remove zero-width spaces typically added during mathfield blot insertion
    wrapper.innerHTML = wrapper.innerHTML.replace(/\u200B|\uFEFF/g, '');

    return wrapper.innerHTML;
  }

}
